import { useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import { useDeviceSize } from "hooks";
import { Slide } from "@chakra-ui/react";
import { useEffect, useMemo, useRef } from "react";
import { useZowiePositionStore } from "stores/ZowiePositionStore";
import { defaultBenefits } from "./StickyBottomBar.config";
import type { StickyBottomBarProps } from "./StickyBottomBar.d";
import StickyBottomBarDesktop from "./StickyBottomBarDesktop";
import StickyBottomBarMobile from "./StickyBottomBarMobile";

const StickyBottomBar = ({ heading, buttonText, benefits, hideBenefits, variant }: StickyBottomBarProps) => {
  const { isDesktop } = useDeviceSize();
  const { isOpen } = useStickyBottomBarContext();
  const bottomBarElement = useRef<HTMLDivElement>(null);
  const { setBottom } = useZowiePositionStore();
  const showCustomBenefits = benefits?.length > 0 && !hideBenefits;

  const benefitsMap = useMemo(() => {
    if (showCustomBenefits) {
      return benefits.map(({ text, icon }) => ({
        text,
        icon,
      }));
    }
    if (!hideBenefits) {
      return defaultBenefits;
    }
    return [];
  }, [showCustomBenefits, benefits, hideBenefits]);

  useEffect(() => {
    if (!isOpen && !isDesktop) {
      setBottom(0);
      return;
    }
    setBottom(Number(bottomBarElement.current?.clientHeight));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDesktop]);

  return (
    <Slide
      in={isOpen}
      direction="bottom"
      style={{ zIndex: 2, transition: ".1s" }}
      data-testid="sticky-bottom-bar"
      ref={bottomBarElement}
    >
      {isDesktop ? (
        <StickyBottomBarDesktop
          buttonText={buttonText}
          heading={heading}
          variant={variant}
          hideBenefits={hideBenefits}
          benefits={benefitsMap}
        />
      ) : (
        <StickyBottomBarMobile
          buttonText={buttonText}
          variant={variant}
          heading={heading}
          benefits={benefitsMap}
          hideBenefits={hideBenefits}
        />
      )}
    </Slide>
  );
};

export default StickyBottomBar;
